import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Avatar, Button, Card, Col, Divider, Empty, Input, List, Row, Segmented,
    Select, Space, Statistic, Table, Tag, Tooltip, Typography,
} from "antd";
import {
    ShopOutlined, MedicineBoxOutlined, SearchOutlined, PlusOutlined, DeleteOutlined, SaveOutlined,
    CheckCircleFilled, WarningFilled, ArrowLeftOutlined, FileTextOutlined, PercentageOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import SideBar from "../../components/SideBar";
import NavBar from "../../components/NavBar";
import Footer from "../../components/Footer";
import axiosInstance from "../../variables/api/axiosInstance";

const { Title, Text } = Typography;

const PRIMARY = "#242166";
const SUCCESS = "#16a34a";
const DANGER = "#dc2626";
const PHARMA = "#0e9f6e";

const fcfa = (n) => `${Number(n || 0).toLocaleString("fr-FR")} FCFA`;
const dossierNum = (d) => d?.numSinistre || d?.numeroSinistreM || d?.numeroSinistreP || "—";
// N° de sinistre de la COMPAGNIE (ancien système) : affiché sous le n° PoolIns pour que les
// utilisateurs encore sur leur ancien référentiel s'y retrouvent. Masqué s'il est absent ou
// identique au n° déjà affiché.
const dossierCie = (d) => {
    const m = (d?.numeroSinistreM || "").trim();
    return m && m !== dossierNum(d) ? m : "";
};
const isPharmacie = (t) => /pharmac/i.test(t || "");
const tel = (e) => (Array.isArray(e?.contact) ? e.contact.filter(Boolean)[0] : e?.contact) || "";
// Libellé distinctif d'une prise en charge (n° + nature) — permet de distinguer plusieurs
// PEC d'un même établissement sur un même dossier.
const pecLabel = (p) => [p?.numeroPriseEnCharge, p?.natureActe]
    .map((x) => (x || "").trim()).filter(Boolean).join(" · ") || "Prise en charge";

/**
 * Règlement multiple des établissements (pharmacies, cliniques, garages…) — v2.
 * Pharmacies mises en avant (filtre type + badge). Modèle de paiement identique aux
 * experts : honoraires + frais accessoires + retenue (0/3/5/20 %, 0 % par défaut ;
 * l'utilisateur ajuste au moment du règlement). Endpoints :
 * GET /api/etablissement, GET /api/sinistre/etablissement/{codeId} (dossiers via prises
 * en charge), POST /api/decaissement/all-etablissements.
 */
const GestionPaiementSinistreEtablissementsV2 = () => {
    const navigate = useNavigate();

    const [etablissements, setEtablissements] = useState([]);
    const [typeFiltre, setTypeFiltre] = useState(null); // null = tous
    const [etablissement, setEtablissement] = useState(null);
    const [tauxRetenue, setTauxRetenue] = useState(0);

    const [pecs, setPecs] = useState([]);
    const [loadingDossiers, setLoadingDossiers] = useState(false);
    const [searchDossier, setSearchDossier] = useState("");

    const [selection, setSelection] = useState([]);
    const [saving, setSaving] = useState(false);

    // ── Chargement des établissements ─────────────────────────────────────────
    useEffect(() => {
        axiosInstance.get("/api/etablissement")
            .then((res) => {
                const list = Array.isArray(res.data) ? res.data : (res.data?.content ?? []);
                setEtablissements(list);
                // Pharmacies mises en avant : filtre par défaut sur les pharmacies si présentes.
                if (list.some((e) => isPharmacie(e.typeEtablissement))) setTypeFiltre("PHARMACIE");
            })
            .catch((e) => { console.error(e); toast.error("Erreur lors du chargement des établissements."); });
    }, []);

    // Types distincts présents, pharmacies en tête.
    const types = useMemo(() => {
        const set = [...new Set(etablissements.map((e) => e.typeEtablissement).filter(Boolean))];
        set.sort((a, b) => (isPharmacie(b) ? 1 : 0) - (isPharmacie(a) ? 1 : 0) || a.localeCompare(b));
        return set;
    }, [etablissements]);

    const segmentedOptions = useMemo(() => ([
        { label: "Tous", value: "__ALL__" },
        ...types.map((t) => ({
            label: isPharmacie(t) ? `💊 ${t}` : t,
            value: t,
        })),
    ]), [types]);

    const handleSelectEtablissement = (codeId) => {
        const et = etablissements.find((x) => x.codeId === codeId) || null;
        setEtablissement(et);
        setSelection([]);
        setSearchDossier("");
        // 0 % de retenue par défaut pour les établissements (ajustable ci-dessous).
        setTauxRetenue(0);
        if (!et) { setPecs([]); return; }

        setLoadingDossiers(true);
        // Prises en charge NON encore réglées de l'établissement (une ligne = une PEC, montant réel).
        axiosInstance.get(`/api/prise-en-charge/etablissement/${et.codeId}`)
            .then((res) => {
                const data = Array.isArray(res.data)
                    ? res.data
                    : res.data?.content ?? res.data?.data ?? res.data?.items ?? [];
                setPecs(data);
            })
            .catch((e) => { console.error(e); toast.error("Erreur lors du chargement des prises en charge."); })
            .finally(() => setLoadingDossiers(false));
    };

    // ── Panier ────────────────────────────────────────────────────────────────
    // Unité = une PRISE EN CHARGE (clé priseEnChargeCodeId). Montant pré-rempli = montant réel
    // de la PEC, ajustable. Plusieurs PEC d'un même dossier restent des lignes distinctes.
    const ajouter = (pec) => {
        if (selection.find((d) => d.priseEnChargeCodeId === pec.priseEnChargeCodeId)) return;
        setSelection((prev) => [...prev, { ...pec, montant: Number(pec.montant) || 0 }]);
    };
    const retirer = (pecCodeId) => setSelection((prev) => prev.filter((d) => d.priseEnChargeCodeId !== pecCodeId));

    // Une ligne est valide si son montant de prise en charge est strictement positif.
    const estValide = (d) => Number(d.montant || 0) > 0;

    const etablissementOptions = useMemo(() => {
        const filtered = typeFiltre && typeFiltre !== "__ALL__"
            ? etablissements.filter((e) => e.typeEtablissement === typeFiltre)
            : etablissements;
        return filtered.map((e) => ({
            value: e.codeId,
            label: e.nom,
            type: e.typeEtablissement,
            adresse: e.adresse,
            tel: tel(e),
        }));
    }, [etablissements, typeFiltre]);

    const pecsDisponibles = useMemo(() => {
        const q = searchDossier.trim().toLowerCase();
        return pecs.filter((p) =>
            !selection.find((s) => s.priseEnChargeCodeId === p.priseEnChargeCodeId) &&
            (!q || dossierNum(p).toLowerCase().includes(q) || dossierCie(p).toLowerCase().includes(q) || pecLabel(p).toLowerCase().includes(q))
        );
    }, [pecs, selection, searchDossier]);

    const totaux = useMemo(() => {
        const totalBrut = selection.reduce((a, d) => a + Number(d.montant || 0), 0);
        const montantRetenue = totalBrut * (tauxRetenue / 100);
        const totalNet = totalBrut - montantRetenue;
        return { totalBrut, montantRetenue, totalNet };
    }, [selection, tauxRetenue]);

    const erreurs = selection.filter((d) => !estValide(d)).length;
    const peutEnregistrer = selection.length > 0 && erreurs === 0 && totaux.totalBrut > 0;

    const enregistrer = () => {
        if (!peutEnregistrer || !etablissement) return;
        setSaving(true);
        const payload = selection.map((d) => ({
            codeId: d.sinistreCodeId,                 // dossier sinistre rattaché à la PEC
            priseEnChargeCodeId: d.priseEnChargeCodeId, // PEC réglée (sera marquée réglée en base)
            honoraires: Number(d.montant || 0),        // MONTANT DE LA PRISE EN CHARGE
            fraisAccessoires: 0,                       // plus de frais accessoires pour les établissements
            retenue: tauxRetenue,
            etablissementCodeId: etablissement.codeId,
        }));
        axiosInstance.post("/api/decaissement/all-etablissements", JSON.stringify(payload), {
            headers: { "Content-Type": "application/json" },
        })
            .then(() => {
                toast.success("Règlement enregistré avec succès.");
                setSelection([]);
                setEtablissement(null);
                setPecs([]);
            })
            .catch((e) => { console.error(e); toast.error("Erreur lors de l'enregistrement du règlement."); })
            .finally(() => setSaving(false));
    };

    // ── Colonnes du tableau « Prises en charge à régler » ─────────────────────
    const columns = [
        {
            title: "Dossier", key: "dossier", width: 150,
            render: (_, d) => (
                <div>
                    <Text strong style={{ fontFamily: "monospace" }}>{dossierNum(d)}</Text>
                    {dossierCie(d) && (
                        <div style={{ fontSize: 11, color: "#9ca3af", fontFamily: "monospace" }}>
                            N°&nbsp;cie&nbsp;: {dossierCie(d)}
                        </div>
                    )}
                    {d.dateSinistre && (
                        <div style={{ fontSize: 12, color: "#9ca3af" }}>
                            {new Date(d.dateSinistre).toLocaleDateString("fr-FR")}
                        </div>
                    )}
                </div>
            ),
        },
        {
            title: "Prise en charge", key: "pec", width: 210,
            render: (_, d) => (
                <div>
                    <Text style={{ fontWeight: 600 }}>{d.numeroPriseEnCharge || "—"}</Text>
                    {d.natureActe && <div style={{ fontSize: 12, color: "#6b7280" }}>{d.natureActe}</div>}
                </div>
            ),
        },
        {
            // Montant NON éditable : c'est le montant réel de la prise en charge (issu des actes).
            title: "Montant de la prise en charge", key: "montant", width: 180, align: "right",
            render: (_, d) => (
                <Text strong style={{ color: PRIMARY }}>{fcfa(d.montant)}</Text>
            ),
        },
        {
            title: "", key: "status", width: 96, align: "right",
            render: (_, d) => (
                estValide(d)
                    ? <Tag color="success" icon={<CheckCircleFilled />} style={{ marginInlineEnd: 0 }}>OK</Tag>
                    : <Tag color="error" icon={<WarningFilled />} style={{ marginInlineEnd: 0 }}>Montant&nbsp;?</Tag>
            ),
        },
        {
            title: "", key: "action", width: 44, align: "center",
            render: (_, d) => (
                <Tooltip title="Retirer">
                    <Button type="text" danger icon={<DeleteOutlined />} onClick={() => retirer(d.priseEnChargeCodeId)} />
                </Tooltip>
            ),
        },
    ];

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"SINISTRES"} />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">

                            {/* ── En-tête ── */}
                            <div style={{
                                background: `linear-gradient(120deg, ${PRIMARY} 0%, #3b378f 100%)`,
                                borderRadius: 16, padding: "22px 26px", marginBottom: 18,
                                display: "flex", justifyContent: "space-between", alignItems: "center",
                                flexWrap: "wrap", gap: 12, boxShadow: "0 6px 18px rgba(36,33,102,0.22)",
                            }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                                    <div style={{
                                        width: 50, height: 50, borderRadius: 12, background: "rgba(255,255,255,0.15)",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                    }}>
                                        <ShopOutlined style={{ fontSize: 26, color: "#fff" }} />
                                    </div>
                                    <div>
                                        <Title level={3} style={{ color: "#fff", margin: 0 }}>Règlement des établissements</Title>
                                        <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                                            Pharmacies, cliniques, garages… — règlement cumulé
                                        </Text>
                                    </div>
                                </div>
                                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} style={{ borderRadius: 8 }}>
                                    Retour
                                </Button>
                            </div>

                            <Row gutter={[18, 18]}>
                                {/* ── Colonne gauche : sélection ── */}
                                <Col xs={24} lg={9}>
                                    <Card
                                        bordered={false} style={{ borderRadius: 14, boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}
                                        title={<Space><ShopOutlined style={{ color: PRIMARY }} /><span>1. Établissement &amp; retenue</span></Space>}
                                    >
                                        {/* Filtre type — pharmacies mises en avant */}
                                        {types.length > 0 && (
                                            <div style={{ marginBottom: 12, overflowX: "auto" }}>
                                                <Segmented
                                                    options={segmentedOptions}
                                                    value={typeFiltre || "__ALL__"}
                                                    onChange={(v) => { setTypeFiltre(v); setEtablissement(null); setPecs([]); setSelection([]); }}
                                                />
                                            </div>
                                        )}

                                        <Text type="secondary" style={{ fontSize: 13 }}>Établissement</Text>
                                        <Select
                                            showSearch allowClear style={{ width: "100%", marginTop: 6 }}
                                            placeholder="Rechercher un établissement…" optionFilterProp="label"
                                            value={etablissement?.codeId} onChange={(v) => handleSelectEtablissement(v)}
                                            options={etablissementOptions}
                                            optionRender={(opt) => (
                                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                                                    <div>
                                                        <div style={{ fontWeight: 600 }}>{opt.data.label}</div>
                                                        {(opt.data.adresse || opt.data.tel) && (
                                                            <div style={{ fontSize: 12, color: "#9ca3af" }}>{[opt.data.adresse, opt.data.tel].filter(Boolean).join(" · ")}</div>
                                                        )}
                                                    </div>
                                                    {isPharmacie(opt.data.type)
                                                        ? <Tag color={PHARMA} icon={<MedicineBoxOutlined />}>Pharmacie</Tag>
                                                        : opt.data.type ? <Tag>{opt.data.type}</Tag> : null}
                                                </div>
                                            )}
                                        />

                                        {etablissement && (
                                            <>
                                                <div style={{
                                                    marginTop: 14, padding: 12, borderRadius: 10,
                                                    background: isPharmacie(etablissement.typeEtablissement) ? "#ecfdf5" : "#f5f5ff",
                                                    display: "flex", alignItems: "center", gap: 12,
                                                }}>
                                                    <Avatar size={42}
                                                        style={{ background: isPharmacie(etablissement.typeEtablissement) ? PHARMA : PRIMARY }}
                                                        icon={isPharmacie(etablissement.typeEtablissement) ? <MedicineBoxOutlined /> : <ShopOutlined />} />
                                                    <div>
                                                        <div style={{ fontWeight: 600, color: PRIMARY }}>
                                                            {etablissement.nom}
                                                            {isPharmacie(etablissement.typeEtablissement) && <Tag color={PHARMA} style={{ marginLeft: 8 }}>Pharmacie</Tag>}
                                                        </div>
                                                        <div style={{ fontSize: 12, color: "#6b7280" }}>
                                                            {[etablissement.typeEtablissement, etablissement.adresse, tel(etablissement)].filter(Boolean).join(" · ")}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div style={{ marginTop: 14 }}>
                                                    <Text type="secondary" style={{ fontSize: 13 }}>
                                                        <PercentageOutlined /> Taux de retenue pour ce règlement
                                                    </Text>
                                                    <Select
                                                        style={{ width: "100%", marginTop: 6 }} value={tauxRetenue}
                                                        onChange={setTauxRetenue}
                                                        options={[
                                                            { value: 0, label: "0 %" },
                                                            { value: 3, label: "3 %" },
                                                            { value: 5, label: "5 %" },
                                                            { value: 20, label: "20 %" },
                                                        ]}
                                                    />
                                                </div>
                                            </>
                                        )}
                                    </Card>

                                    {/* Prises en charge disponibles */}
                                    <Card
                                        bordered={false}
                                        style={{ borderRadius: 14, marginTop: 18, boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}
                                        title={
                                            <Space>
                                                <FileTextOutlined style={{ color: PRIMARY }} />
                                                <span>2. Prises en charge disponibles</span>
                                                {etablissement && <Tag color="default">{pecsDisponibles.length}</Tag>}
                                            </Space>
                                        }
                                    >
                                        {!etablissement ? (
                                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sélectionnez d'abord un établissement" />
                                        ) : (
                                            <>
                                                <Input
                                                    allowClear prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
                                                    placeholder="Rechercher (dossier / prise en charge)…" value={searchDossier}
                                                    onChange={(e) => setSearchDossier(e.target.value)} style={{ marginBottom: 12 }}
                                                />
                                                <List
                                                    loading={loadingDossiers}
                                                    dataSource={pecsDisponibles}
                                                    locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={pecs.length === 0 ? "Aucune prise en charge à régler pour cet établissement" : "Toutes les prises en charge ont été ajoutées"} /> }}
                                                    style={{ maxHeight: 360, overflowY: "auto" }}
                                                    renderItem={(p) => (
                                                        <List.Item
                                                            style={{ padding: "10px 4px" }}
                                                            actions={[
                                                                <Button key="add" type="primary" ghost size="small" icon={<PlusOutlined />} onClick={() => ajouter(p)}>
                                                                    Ajouter
                                                                </Button>,
                                                            ]}
                                                        >
                                                            <List.Item.Meta
                                                                title={
                                                                    <span>
                                                                        <span style={{ fontFamily: "monospace" }}>{dossierNum(p)}</span>
                                                                        {p.numeroPriseEnCharge && <Tag style={{ marginLeft: 8 }}>{p.numeroPriseEnCharge}</Tag>}
                                                                        {dossierCie(p) && (
                                                                            <span style={{ display: "block", fontSize: 11, color: "#9ca3af", fontFamily: "monospace", fontWeight: 400, marginTop: 2 }}>
                                                                                N°&nbsp;cie&nbsp;: {dossierCie(p)}
                                                                            </span>
                                                                        )}
                                                                    </span>
                                                                }
                                                                description={
                                                                    <span style={{ fontSize: 12 }}>
                                                                        {p.natureActe ? `${p.natureActe} · ` : ""}
                                                                        <Text strong style={{ color: PRIMARY }}>{fcfa(p.montant)}</Text>
                                                                    </span>
                                                                }
                                                            />
                                                        </List.Item>
                                                    )}
                                                />
                                            </>
                                        )}
                                    </Card>
                                </Col>

                                {/* ── Colonne droite : panier + récap ── */}
                                <Col xs={24} lg={15}>
                                    <Card
                                        bordered={false} style={{ borderRadius: 14, boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}
                                        title={
                                            <Space>
                                                <SaveOutlined style={{ color: PRIMARY }} />
                                                <span>3. Prises en charge à régler</span>
                                                <Tag color={selection.length ? "processing" : "default"}>{selection.length}</Tag>
                                                {erreurs > 0 && <Tag color="error">{erreurs} en erreur</Tag>}
                                            </Space>
                                        }
                                    >
                                        <Table
                                            rowKey="priseEnChargeCodeId" size="middle" columns={columns} dataSource={selection}
                                            pagination={false} scroll={{ x: "max-content" }}
                                            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Ajoutez des prises en charge depuis la liste de gauche" /> }}
                                        />

                                        {selection.length > 0 && (
                                            <>
                                                <Divider style={{ margin: "18px 0" }} />
                                                <Row gutter={[16, 16]} align="middle">
                                                    <Col xs={24} md={16}>
                                                        <div style={{
                                                            background: "#f8fafc", borderRadius: 12, padding: "16px 18px",
                                                            border: "1px solid #eef2f7",
                                                        }}>
                                                            <Row gutter={[16, 12]}>
                                                                <Col span={12}><Statistic title="Nombre de prises en charge" value={selection.length} valueStyle={{ fontSize: 16 }} /></Col>
                                                                <Col span={12}><Statistic title="Montant total des prises en charge" value={totaux.totalBrut} suffix="FCFA" valueStyle={{ fontSize: 16, color: PRIMARY }} groupSeparator=" " /></Col>
                                                                <Col span={12}><Statistic title={`Retenue (${tauxRetenue === 0 ? "sans retenue" : `${tauxRetenue}%`})`} value={totaux.montantRetenue} prefix="-" suffix="FCFA" valueStyle={{ fontSize: 16, color: DANGER }} groupSeparator=" " /></Col>
                                                            </Row>
                                                        </div>
                                                    </Col>
                                                    <Col xs={24} md={8}>
                                                        <div style={{
                                                            background: `linear-gradient(135deg, ${SUCCESS} 0%, #15803d 100%)`,
                                                            borderRadius: 12, padding: "18px 20px", color: "#fff", textAlign: "center",
                                                        }}>
                                                            <div style={{ fontSize: 13, opacity: 0.9 }}>Montant net à payer</div>
                                                            <div style={{ fontSize: 26, fontWeight: 700, marginTop: 4 }}>{fcfa(totaux.totalNet)}</div>
                                                        </div>
                                                    </Col>
                                                </Row>

                                                <div style={{ marginTop: 18, textAlign: "right" }}>
                                                    <Button
                                                        type="primary" size="large" icon={<SaveOutlined />}
                                                        loading={saving} disabled={!peutEnregistrer} onClick={enregistrer}
                                                        style={{ background: peutEnregistrer ? SUCCESS : undefined, border: "none", borderRadius: 10, fontWeight: 600 }}
                                                    >
                                                        Enregistrer le règlement
                                                    </Button>
                                                    {erreurs > 0 && (
                                                        <div style={{ marginTop: 8, color: DANGER, fontSize: 13 }}>
                                                            <WarningFilled /> {erreurs} prise(s) en charge sans montant valide.
                                                        </div>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </Card>
                                </Col>
                            </Row>
                        </div>

                        <Footer />
                        <div className="content-backdrop fade"></div>
                    </div>
                </div>
            </div>
            <div className="layout-overlay layout-menu-toggle"></div>
            <div className="drag-target"></div>
        </div>
    );
};

export default GestionPaiementSinistreEtablissementsV2;
